import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import type { FollowUpConfig } from 'services/types/followUpConfig';
import { NAVY, RADIUS_CARD, SHADOW, YELLOW } from 'styles/tokens';

import CreateFollowUpConfig from './components/CreateFollowUpConfig';
import SelectFollowUp from './components/SelectFollowUp';
import { sectionGap, sectionSubtitleSx, sectionTitleSx } from './styles';

interface SelectFollowUpProps {
  businessUnitId: number | null;
  selectFollowUp: (followUpId: FollowUpConfig) => void;
}

type CampaignType = 'new' | 'existing';

const OPTIONS: { value: CampaignType; icon: React.ReactNode; label: string; description: string }[] = [
  {
    value: 'existing',
    icon: <ListAltIcon sx={{ fontSize: 28 }} />,
    label: 'Encuesta existente',
    description: 'Selecciona una encuesta que ya hayas configurado',
  },
  {
    value: 'new',
    icon: <AddCircleOutlinedIcon sx={{ fontSize: 28 }} />,
    label: 'Nueva encuesta',
    description: 'Crea una encuesta desde cero con una nueva configuración',
  },
];

const SelectOrCreateFollowUp = ({ businessUnitId, selectFollowUp }: SelectFollowUpProps) => {
  const [campaignType, setCampaignType] = useState<CampaignType>('existing');

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {!businessUnitId && (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ ...sectionTitleSx, color: alpha(NAVY, 0.55) }}>
            Selecciona una unidad de negocio para ver las encuestas
          </Typography>
        </Box>
      )}
      {businessUnitId && (
        <Box>
          <Box sx={{ mb: sectionGap }}>
            <Typography sx={sectionTitleSx}>Tipo de encuesta</Typography>
            <Typography sx={sectionSubtitleSx}>
              Si ya tienes una encuesta existente, puedes seleccionarla aquí. Si no, puedes crear una nueva.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: sectionGap }}>
            {OPTIONS.map((opt) => {
              const isActive = campaignType === opt.value;
              return (
                <Box
                  key={opt.value}
                  onClick={() => setCampaignType(opt.value)}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: RADIUS_CARD,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: isActive ? alpha(YELLOW, 0.08) : alpha(NAVY, 0.02),
                    boxShadow: isActive ? `0 0 0 2px ${YELLOW}, ${SHADOW}` : `0 0 0 1px ${alpha(NAVY, 0.12)}`,
                    opacity: isActive ? 1 : 0.65,
                    '&:hover': {
                      backgroundColor: isActive ? alpha(YELLOW, 0.08) : alpha(NAVY, 0.05),
                      boxShadow: isActive ? `0 0 0 2px ${YELLOW}, ${SHADOW}` : `0 0 0 1px ${alpha(NAVY, 0.25)}`,
                      opacity: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      color: isActive ? NAVY : alpha(NAVY, 0.4),
                      display: 'flex',
                      transition: 'color 0.2s ease',
                    }}
                  >
                    {opt.icon}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        color: isActive ? NAVY : alpha(NAVY, 0.6),
                        lineHeight: 1.3,
                      }}
                    >
                      {opt.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        fontSize: '0.8rem',
                        color: alpha(NAVY, 0.5),
                        lineHeight: 1.3,
                        mt: 0.25,
                      }}
                    >
                      {opt.description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Box>
            {campaignType === 'existing' && (
              <SelectFollowUp businessUnitId={businessUnitId} selectFollowUp={selectFollowUp} />
            )}
            {campaignType === 'new' && (
              <CreateFollowUpConfig businessUnitId={businessUnitId} selectFollowUp={selectFollowUp} />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SelectOrCreateFollowUp;
